import AddCardModal from "../../Profile/AddCardModal/AddCardModal";
import { RootState } from "../../../../Redux/store";
import React, { useMemo, useState } from "react";
import styles from "./SelectCard.module.scss";
import { useSelector } from "react-redux";
import API from "../../../../API/API";
import axios from "axios";

interface BaseProps {
  selectedCard: string;
  setSelectedCard: React.Dispatch<React.SetStateAction<string>>;
  packageData?: any;
  label?: string;
  email?: string;
}

type Props =
  | (BaseProps & {
      voucherEnabled: true;
      voucherCode: string;
      setVoucherCode: React.Dispatch<React.SetStateAction<string>>;
      voucherSuccess: any;
      setVoucherSuccess: React.Dispatch<React.SetStateAction<any>>;
    })
  | (BaseProps & {
      voucherEnabled?: false;
      voucherCode?: never;
      setVoucherCode?: never;
      voucherSuccess?: never;
      setVoucherSuccess?: never;
    });

function SelectCard(props: Props) {
  const {
    selectedCard,
    setSelectedCard,
    label,
    packageData,
    voucherCode,
    setVoucherCode,
    voucherSuccess,
    setVoucherSuccess,
    voucherEnabled,
    email,
  } = props;
  const { profileData } = useSelector((state: RootState) => state.profile);
  const [addNewCardModalOpen, setAddNewCardModalOpen] = useState(false);
  const [errors, setErrors] = useState<{
    voucherCode: string;
  }>({
    voucherCode: "",
  });

  const applyVoucherHandler = async () => {
    if (voucherCode?.trim() !== "") {
      setErrors((prev) => ({
        ...prev,
        voucherCode: "",
      }));
      try {
        const { data } = await axios({
          url: `${API.members.validateVoucher.url}?voucherCode=${voucherCode}&email=${email}&voucherType=1`,
          method: API.members.validateVoucher.method,
        });
        setVoucherSuccess?.(data);
      } catch (error: any) {
        if (error?.response?.data?.message) {
          setErrors((prev) => ({
            ...prev,
            voucherCode: error?.response?.data?.message ?? "",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            voucherCode: "Something went wrong",
          }));
        }
      }
    } else {
      setErrors((prev) => ({
        ...prev,
        voucherCode: "Voucher code is required",
      }));
    }
  };

  const removeVoucherHandler = () => {
    setVoucherSuccess?.(null);
    setVoucherCode?.("");
  };

  const monthlySubtotalBeforeVoucher = useMemo(() => {
    const packageRate =
      packageData?.discountRate > 0
        ? Number(packageData?.totalRates ?? 0) -
          Number(packageData?.discountRate ?? 0)
        : Number(packageData?.totalRates ?? 0);

    return packageRate;
  }, [packageData]);

  const voucherDiscountAmount = useMemo(() => {
    if (!voucherSuccess?.success || !voucherSuccess?.voucher) return 0;

    const { percent, maxDiscount } = voucherSuccess.voucher;
    const percentValue = Number(percent ?? 0);

    let discount = (monthlySubtotalBeforeVoucher * percentValue) / 100;

    if (maxDiscount !== null && maxDiscount !== undefined) {
      discount = Math.min(discount, Number(maxDiscount));
    }

    return Number(discount.toFixed(2));
  }, [monthlySubtotalBeforeVoucher, voucherSuccess]);

  return (
    <React.Fragment>
      <AddCardModal
        isOpen={addNewCardModalOpen}
        setIsOpen={setAddNewCardModalOpen}
      />

      <div className={styles.mainContainer}>
        <div className={styles.headingContainer}>
          <p className={styles.label}>{label || "Create Booking"}</p>
        </div>
        {packageData && voucherEnabled && (
          <div className={styles.packageInfoWrapper}>
            <div className={styles.packageInfoContainer}>
              <p className={styles.label}>Package Selected</p>
              <p className={styles.packageName}>
                {packageData?.packageName} - $
                {packageData?.discountRate > 0
                  ? packageData?.totalRates - packageData?.discountRate
                  : packageData?.totalRates}
                /mo
              </p>
            </div>
            {!voucherSuccess ? (
              <div className={styles.couponInputContainer}>
                <div className={styles.couponInputBox}>
                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Enter Voucher Code"
                    value={voucherCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s/g, "");
                      setVoucherCode?.(value);
                    }}
                  />
                  <p className={styles.error}>{errors?.voucherCode}</p>
                </div>
                <button className={styles.button} onClick={applyVoucherHandler}>
                  Apply
                </button>
              </div>
            ) : (
              <div className={styles.couponSuccessContainer}>
                <p className={styles.heading}>Coupon Applied</p>
                <div className={styles.voucherContainer}>
                  <p className={styles.voucherCode}>{voucherCode}</p>
                  <p className={styles.voucherDiscount}>
                    -${voucherDiscountAmount}
                  </p>
                </div>
                <div className={styles.buttonContainer}>
                  <button
                    className={styles.button}
                    onClick={removeVoucherHandler}
                  >
                    Remove Voucher
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className={styles.selectCardBodyContainer}>
          <div className={styles.inputContainer}>
            <p className={styles.label}>Select Card</p>
            <select
              className={styles.input}
              value={selectedCard}
              onChange={(e) => setSelectedCard(e.target.value)}
            >
              {!selectedCard && <option value="">Select Card</option>}
              {profileData?.memberCCInfo?.map((card: any, i: number) => (
                <option key={i} value={card?.id}>
                  XXX - {card?.cardNumber}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.buttonContainer}>
            <button
              className={styles.button}
              onClick={() => setAddNewCardModalOpen(true)}
            >
              Add new card
            </button>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default SelectCard;
